import { apiClient } from '../../app/api/client';

import type {
  AICurveExtractionRunRead,
  ChartRegion,
  CurveDraftDocument,
  CurveImportCapabilities,
  CurveImportPage,
  CurveImportRead,
  CurveKind,
  CurveSetRevisionRead,
  CurveTraceDraft,
  DiscardImportResult,
  ProcessingJobRead,
  VerifyImportInput,
  VerifyImportResult,
} from './types';

export interface CurveImportApi {
  archiveImport(id: string, reason: string): Promise<CurveImportRead>;
  beginCorrection(id: string, reason: string): Promise<CurveImportRead>;
  cancelJob(id: string): Promise<ProcessingJobRead>;
  capabilities(): Promise<CurveImportCapabilities>;
  createImport(file: File, maxFileBytes: number): Promise<CurveImportRead>;
  findSourceImports(file: File, maxFileBytes: number, offset?: number): Promise<CurveImportPage>;
  discardImport(
    id: string,
    confirmation: {
      expected_original_filename: string;
      expected_source_sha256: string;
      reason: string;
    },
  ): Promise<DiscardImportResult>;
  getImport(id: string): Promise<CurveImportRead>;
  getJob(id: string): Promise<ProcessingJobRead>;
  listRevisions(id: string): Promise<CurveSetRevisionRead[]>;
  listImports(): Promise<CurveImportPage>;
  materializeAiSuggestions(
    id: string,
    localRequestId: string,
    responseSha256: string,
  ): Promise<CurveImportRead>;
  pageUrl(id: string, page: number): string;
  rejectImport(id: string, reason: string): Promise<CurveImportRead>;
  requestAiSuggestions(
    id: string,
    selectedPages: number[],
  ): Promise<AICurveExtractionRunRead>;
  resampleTrace(trace: CurveTraceDraft, pointCount: number): Promise<CurveTraceDraft>;
  startParse(
    id: string,
    enableOcr: boolean,
    selectedPages: number[] | null,
  ): Promise<ProcessingJobRead>;
  suggestTrace(
    id: string,
    input: {
      chart_region: ChartRegion;
      kind: CurveKind;
      label: string;
      speed_rpm: number | null;
    },
  ): Promise<CurveTraceDraft>;
  updateDraft(id: string, document: CurveDraftDocument): Promise<CurveImportRead>;
  verifyImport(id: string, input: VerifyImportInput): Promise<VerifyImportResult>;
}

const mediaTypesByExtension = {
  csv: 'text/csv',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  pdf: 'application/pdf',
  png: 'image/png',
} as const;

export function canonicalMediaType(file: File): string {
  const extension = file.name.split('.').at(-1)?.toLowerCase();
  if (extension === undefined || !(extension in mediaTypesByExtension)) {
    throw new Error('Choose a PDF, PNG, JPEG, or CSV pump-curve source.');
  }
  return mediaTypesByExtension[extension as keyof typeof mediaTypesByExtension];
}

function fileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('The selected file could not be read.'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('The selected file could not be encoded.'));
        return;
      }
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}

function validateSourceFile(file: File, maxFileBytes: number): string {
  if (file.size > maxFileBytes) {
    throw new Error(
      `The selected file is ${file.size.toLocaleString()} bytes; the local limit is ${maxFileBytes.toLocaleString()} bytes.`,
    );
  }
  return canonicalMediaType(file);
}

const sourceHashes = new WeakMap<File, Promise<string>>();

function exactSourceHash(file: File): Promise<string> {
  const cached = sourceHashes.get(file);
  if (cached !== undefined) return cached;
  const hash = new Promise<ArrayBuffer>((resolve, reject) => {
    if (globalThis.crypto?.subtle === undefined) {
      reject(new Error('This browser cannot check for an existing source. Use a secure HTTPS connection and retry, or explicitly create a separate import.'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('The selected file could not be read.'));
    reader.onabort = () => reject(new Error('Reading the selected file was cancelled.'));
    reader.onload = () => {
      if (!(reader.result instanceof ArrayBuffer)) {
        reject(new Error('The selected file could not be read.'));
        return;
      }
      resolve(reader.result);
    };
    reader.readAsArrayBuffer(file);
  }).then(async (bytes) => {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  });
  sourceHashes.set(file, hash);
  void hash.catch(() => sourceHashes.delete(file));
  return hash;
}

export const curveImportApi: CurveImportApi = {
  archiveImport: (id, reason) =>
    apiClient.post(`/curve-import/imports/${id}/archive`, { json: { reason } }),
  beginCorrection: (id, reason) =>
    apiClient.post(`/curve-import/imports/${id}/begin-correction`, {
      json: { reason },
    }),
  cancelJob: (id) => apiClient.post(`/curve-import/jobs/${id}/cancel`),
  capabilities: () => apiClient.get('/curve-import/capabilities'),
  createImport: async (file, maxFileBytes) => {
    const mediaType = validateSourceFile(file, maxFileBytes);
    return apiClient.post('/curve-import/imports', {
      json: {
        content_base64: await fileAsBase64(file),
        filename: file.name,
        media_type: mediaType,
      },
    });
  },
  findSourceImports: async (file, maxFileBytes, offset = 0) => {
    validateSourceFile(file, maxFileBytes);
    if (!Number.isSafeInteger(offset) || offset < 0) {
      throw new Error('The existing-source page offset must be a non-negative integer.');
    }
    const sourceSha256 = await exactSourceHash(file);
    return apiClient.get(`/curve-import/imports?source_sha256=${sourceSha256}&offset=${offset}&limit=50`);
  },
  discardImport: (id, confirmation) =>
    apiClient.delete(`/curve-import/imports/${id}`, {
      json: { confirm_permanent_delete: true, ...confirmation },
    }),
  getImport: (id) => apiClient.get(`/curve-import/imports/${id}`),
  getJob: (id) => apiClient.get(`/curve-import/jobs/${id}`),
  listRevisions: (id) => apiClient.get(`/curve-import/imports/${id}/revisions`),
  listImports: () => apiClient.get('/curve-import/imports?limit=100'),
  materializeAiSuggestions: (id, localRequestId, responseSha256) =>
    apiClient.post(
      `/curve-import/imports/${id}/ai-suggestions/${localRequestId}/materialize`,
      { json: { response_sha256: responseSha256 } },
    ),
  pageUrl: (id, page) => `/api/v1/curve-import/imports/${id}/pages/${page}`,
  rejectImport: (id, reason) =>
    apiClient.post(`/curve-import/imports/${id}/reject`, { json: { reason } }),
  resampleTrace: (trace, pointCount) =>
    apiClient.post('/curve-import/traces/resample', {
      json: { point_count: pointCount, trace },
    }),
  requestAiSuggestions: (id, selectedPages) => {
    if (selectedPages.length === 0 || selectedPages.some((page) => !Number.isSafeInteger(page) || page < 1)) {
      return Promise.reject(new Error('Select at least one valid source page before requesting AI analysis.'));
    }
    return apiClient.post(`/curve-import/imports/${id}/ai-suggestions`, {
      json: {
        authorization_method: 'analyze_action',
        privacy_policy_version: 'curve-analysis-privacy/3.0',
        selected_pages: [...new Set(selectedPages)].sort((left, right) => left - right),
      },
    });
  },
  startParse: (id, enableOcr, selectedPages) =>
    apiClient.post(`/curve-import/imports/${id}/parse-jobs`, {
      json: { enable_ocr: enableOcr, selected_pages: selectedPages },
    }),
  suggestTrace: (id, input) =>
    apiClient.post(`/curve-import/imports/${id}/trace-suggestions`, {
      json: input,
    }),
  updateDraft: (id, document) =>
    apiClient.put(`/curve-import/imports/${id}/draft`, { json: document }),
  verifyImport: (id, input) =>
    apiClient.post(`/curve-import/imports/${id}/verify`, { json: input }),
};
